/*
 * Copyright 2018 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { get12HourFrom24Hour, get24HourFrom12Hour } from './dateUtils';

/** describes a component of time. `H:MM:SS.Units.MS` */

export const Units = {
  HOUR_24: 'hour24',
  HOUR_12: 'hour12',
  MINUTE: 'minute',
  SECOND: 'second',
  MS: 'ms',
};

// currently only support MINUTE
export const TimePrecision = {
  // MILLISECOND: 'millisecond',
  MINUTE: 'minute',
  // SECOND: 'second',
};

export const ClockFormats = {
  AM: 'am',
  PM: 'pm',
};

/** Returns the given time unit component of the date. */
export const getTimeUnit = (unit, date) => {
  switch (unit) {
    case Units.HOUR_24:
      return date.getHours();
    case Units.HOUR_12:
      return get12HourFrom24Hour(date.getHours());
    case Units.MINUTE:
      return date.getMinutes();
    case Units.SECOND:
      return date.getSeconds();
    case Units.MS:
      return date.getMilliseconds();
    default:
      throw Error('Invalid TimeUnit');
  }
};

/** Sets the given time unit to the given time in date object. Modifies given `date` object and returns it. */
export const setTimeUnit = (unit, time, date, isPm) => {
  switch (unit) {
    case Units.HOUR_24:
      date.setHours(time);
      break;
    case Units.HOUR_12:
      date.setHours(get24HourFrom12Hour(time, isPm));
      break;
    case Units.MINUTE:
      date.setMinutes(time);
      break;
    case Units.SECOND:
      date.setSeconds(time);
      break;
    case Units.MS:
      date.setMilliseconds(time);
      break;
    default:
      throw Error('Invalid TimeUnit');
  }
  return date;
};

/** Returns true if `time` is a valid value */
export const isTimeUnitValid = (unit, time) => {
  return time != null && !isNaN(time) && getTimeUnitMin(unit) <= time && time <= getTimeUnitMax(unit);
};

/** If unit of time is greater than max, returns min. If less than min, returns max. Otherwise, returns time. */
export const wrapTimeAtUnit = (unit, time) => {
  const max = getTimeUnitMax(unit);
  const min = getTimeUnitMin(unit);

  if (time > max) {
    return min;
  } else if (time < min) {
    return max;
  }
  return time;
};

export const getTimeUnitClassName = unit => {
  return TimeUnitMetadata[unit].className;
};

export const getTimeUnitMax = unit => {
  return TimeUnitMetadata[unit].max;
};

export const getTimeUnitMin = unit => {
  return TimeUnitMetadata[unit].min;
};

export const getDefaultMinTime = () => {
  return new Date(0, 0, 0, DEFAULT_MIN_HOUR, DEFAULT_MIN_MINUTE, DEFAULT_MIN_SECOND, DEFAULT_MIN_MILLISECOND);
};

export const getDefaultMaxTime = () => {
  return new Date(0, 0, 0, DEFAULT_MAX_HOUR, DEFAULT_MAX_MINUTE, DEFAULT_MAX_SECOND, DEFAULT_MAX_MILLISECOND);
};

const DEFAULT_MIN_HOUR = 0;
const MERIDIEM_MIN_HOUR = 1;
const DEFAULT_MIN_MINUTE = 0;
const DEFAULT_MIN_SECOND = 0;
const DEFAULT_MIN_MILLISECOND = 0;

const DEFAULT_MAX_HOUR = 23;
const MERIDIEM_MAX_HOUR = 12;
const DEFAULT_MAX_MINUTE = 59;
const DEFAULT_MAX_SECOND = 59;
const DEFAULT_MAX_MILLISECOND = 999;

/**
 * A datastore (internal to this file) mapping TimeUnits to useful information about them.
 * Use the `get*` methods above to access these fields.
 */
const TimeUnitMetadata = {
  [Units.HOUR_24]: {
    max: DEFAULT_MAX_HOUR,
    min: DEFAULT_MIN_HOUR,
  },
  [Units.HOUR_12]: {
    max: MERIDIEM_MAX_HOUR,
    min: MERIDIEM_MIN_HOUR,
  },
  [Units.MINUTE]: {
    max: DEFAULT_MAX_MINUTE,
    min: DEFAULT_MIN_MINUTE,
  },
  [Units.SECOND]: {
    max: DEFAULT_MAX_SECOND,
    min: DEFAULT_MIN_SECOND,
  },
  [Units.MS]: {
    max: DEFAULT_MAX_MILLISECOND,
    min: DEFAULT_MIN_MILLISECOND,
  },
};
